import {useQuery} from "@tanstack/react-query"
import moment from "moment"
import {getFullName, handleError, toDateOnly} from "helpers"
import {useCalendarEventsViewSettings} from "hooks/useCalendarEventViewSettings"
import {find, forEach, get, isEmpty, keyBy} from "lodash"
import {useEffect, useState} from "react"
import {calendarService} from "services"
import {useModel} from "hooks"
import {CalendarEventType, CalendarStaticFilter, LinkedCalendarInfo, StaticOption} from "types/calendar"
import {
    checkIsKeepSystemDateEvent,
    checkIsTermMajorSystemEvent,
    getEndDateTime,
    getStartDateTime
} from "helpers/calendar"
import {useGetExtraEventInfo} from "./useGetExtraEventInfo"
import {ShiftWorkWeek, WorkWeekEventType} from "types/attendance"
import RRule from "rrule"
import {v4 as uuid} from "uuid"

const DEFAULT_STATIC_OPTIONS: StaticOption[] = [
    {id: CalendarStaticFilter.Term, name: "Public Terms", isShow: true},
    {id: CalendarStaticFilter.ProgramStart, name: "Public Program Start", isShow: true},
    {id: CalendarStaticFilter.ProgramEnd, name: "Public Program End", isShow: true},
    {id: CalendarStaticFilter.WorkSchedules, name: "Public Work Schedules", isShow: true},
    {id: CalendarStaticFilter.Booked, name: "Booked Events", isShow: true},
    {id: CalendarStaticFilter.CourseSchedules, name: "Course Schedules", isShow: true},
    {id: CalendarStaticFilter.Invites, name: "Invites", isShow: true}
]

const RRULE_WEEKDAYS = [
    RRule.SU, // 0 = Sunday
    RRule.MO,
    RRule.TU,
    RRule.WE,
    RRule.TH,
    RRule.FR,
    RRule.SA
]

export const useGetCalendarEvents = (data?: {
    shiftData?: {
        shiftWorkWeeks: ShiftWorkWeek[]
        startDate: string
        contractEndDate: string
        projectedEndDate: string
    }
}) => {
    const {shiftData} = data || {}
    const model = useModel()
    const userId = +get(model, "user.id")
    const [dateRange, setDateRange] = useState(null)
    const [events, setEvents] = useState([])
    const {settings, isLoadingSettings} = useCalendarEventsViewSettings()
    const [linkedCalendars, setLinkedCalendars] = useState<LinkedCalendarInfo[]>([])
    const [staticOptions, setStaticOptions] = useState<StaticOption[]>([])
    const {getEventColor, getModuleTitle, getSubModuleTitle, getUsers} = useGetExtraEventInfo()

    const {data: myCalendars, isLoading: isLoadingCalendar} = useQuery(
        ["my-calendars", model.viewOtherUserCalendar?.id],
        () => calendarService.getMyCalendars({viewOtherUserId: model.viewOtherUserCalendar?.id}),
        {
            onError: (error) => handleError(error),
            initialData: [],
            onSuccess: (data) => {
                const linkedCalendars = data
                    .filter((item) => item.is_primary || !isEmpty(item.integration_data) || item.type === "group")
                    .map((item) => ({
                        isPrimary: item.is_primary,
                        type: item.type,
                        id:
                            item.type === "group"
                                ? item.calendar_id
                                : !item.is_primary
                                ? get(item, "integration_data.id")
                                : null,
                        calendarId: item.calendar_id,
                        name: item.is_primary ? getFullName(model.viewOtherUserCalendar || model.user) : item.name,
                        provider: !item.is_primary ? get(item, "integration_data.account.provider") : null,
                        requiresReconnect: !item.is_primary && !!get(item, "integration_data.requiresReconnect"),
                        isShow: item.is_primary || !get(item, "integration_data.requiresReconnect")
                    }))
                setLinkedCalendars(linkedCalendars)
            }
        }
    )

    const {refetch: reloadEvents, isFetching} = useQuery(
        ["calendar-events", staticOptions, dateRange, linkedCalendars],
        () => getEvents(),
        {
            enabled: !isEmpty(staticOptions) && !isEmpty(dateRange) && !isEmpty(linkedCalendars),
            onSuccess: (response) => {
                const data = convertEvents(response)
                const schedules = getDailySchedules()
                const allEvents = [...data, ...schedules]
                setEvents(allEvents)
            },
            onError: (error) => handleError(error)
        }
    )

    useEffect(() => {
        if (!isEmpty(settings)) {
            const newStaticOptions = DEFAULT_STATIC_OPTIONS.filter((item) => {
                switch (item.id) {
                    case CalendarStaticFilter.ProgramStart:
                        return settings.programStart
                    case CalendarStaticFilter.ProgramEnd:
                        return settings.programEnd
                    case CalendarStaticFilter.Term:
                        return settings.termStartAndEnd
                    default:
                        return true
                }
            })
            setStaticOptions(newStaticOptions)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings])

    const getEvents = async () => {
        const {startStr: from, endStr: to} = dateRange
        const utcFrom = moment(from).utc().format()
        const utcTo = moment(to).utc().format()
        const checkedStaticOptionIds = staticOptions.filter((item) => item.isShow).map((item) => item.id)
        const checkedStaticFilter = Object.fromEntries(checkedStaticOptionIds.map((item) => [item, true]))
        const calendarIds = linkedCalendars.filter((item) => item.isShow).map((item) => item.calendarId)
        const filter = {
            from: utcFrom,
            to: utcTo,
            owner_user_ids: [userId],
            calendar_ids: calendarIds,
            ...checkedStaticFilter
        }
        const allEvents = await calendarService.getEvents({filter}, {viewOtherUserId: model.viewOtherUserCalendar?.id})
        let userIds = []
        if (!isEmpty(allEvents)) {
            const newUserIds = allEvents
                .filter((item) => item.owner_user_id && item.owner_user_id !== userId)
                .map((event) => event.owner_user_id)
            userIds = [...userIds, ...newUserIds]
        }
        const users = await getUsers(userIds)
        const usersKeyById = keyBy(users, "id")
        forEach(allEvents, (event) => {
            const isCurrentUser = event.owner_user_id === userId
            event.ownerFullName = isCurrentUser
                ? getFullName(model.user)
                : getFullName(usersKeyById[event.owner_user_id])
        })

        return allEvents
    }

    const getDailySchedules = () => {
        if (!shiftData) {
            return []
        }

        const allEvents = []
        const {shiftWorkWeeks, startDate, contractEndDate, projectedEndDate} = shiftData

        if (!startDate || (!projectedEndDate && !contractEndDate)) {
            return []
        }

        for (let shiftWorkWeek of shiftWorkWeeks) {
            const workWeek = shiftWorkWeek.workWeek ?? []
            const dailySchedules = workWeek
                .map((workDay) => {
                    if (workDay.allDay) {
                        workDay.startTime = "00:00"
                        workDay.endTime = "24:00"
                    }
                    return workDay
                })
                .filter((item) => item.startTime && item.endTime)

            for (let dailySchedule of dailySchedules) {
                const [startHour, startMinute] = dailySchedule.startTime.split(":").map(Number)
                const [endHour, endMinute] = dailySchedule.endTime.split(":").map(Number)
                const allDay = dailySchedule.allDay
                const rule = new RRule({
                    freq: RRule.WEEKLY,
                    dtstart: moment(toDateOnly(startDate)).toDate(),
                    until: moment(
                        projectedEndDate ? toDateOnly(projectedEndDate) : toDateOnly(contractEndDate)
                    ).toDate(),
                    byweekday: RRULE_WEEKDAYS[dailySchedule.dayOfWeek]
                })

                const dates = rule.all()
                dates.forEach((date) => {
                    const eventStart = moment(toDateOnly(date))
                    const eventEnd = moment(toDateOnly(date))
                    eventStart.set({
                        hour: startHour,
                        minute: startMinute,
                        second: 0,
                        ms: 0
                    })

                    eventEnd.set({
                        hour: endHour,
                        minute: endMinute,
                        second: 0,
                        ms: 0
                    })

                    allEvents.push({
                        name: shiftWorkWeek.name,
                        json_data: {
                            eventType: WorkWeekEventType.Daily,
                            sub_module_id: null,
                            allDay
                        },
                        object_id: uuid(),
                        start_at_utc: moment(eventStart).utc(),
                        end_at_utc: moment(eventEnd).utc(),
                        dayOfWeek: dailySchedule.dayOfWeek
                    })
                })
            }
        }
        return allEvents
    }

    const convertEvents = (initialEvents) => {
        const timezone = moment.tz.guess()
        return (initialEvents || []).map((event, index) => {
            const originalColor = event?.json_data?.color || "#1e90ff"
            const reminders = (event?.json_data?.notifications ?? []).map((value) => ({
                value,
                label: `${moment.duration(value / 60, "minutes").humanize()} before`
            }))
            const withSMS = !!event?.json_data?.with_sms
            const withPushNotification = !!event?.json_data?.with_push_notification
            const withEmail = !!event?.json_data?.with_email
            const eventSignInRequired = !!event?.json_data?.eventSignInRequired
            const eventTrackAttendanceMinutes = !!event?.json_data?.eventTrackAttendanceMinutes
            const attendedChangeToStatus = !!event?.json_data?.attendedChangeToStatus
            const notAttendedChangeToStatus = !!event?.json_data?.notAttendedChangeToStatus
            const completionWorkflow = !!event?.json_data?.completionWorkflow
            const {borderColor, color} = getEventColor(event)
            const allDay =
                checkIsTermMajorSystemEvent(event.type) || event?.json_data?.all_day || event?.json_data?.allDay
            const startDateTime =
                checkIsKeepSystemDateEvent(event.type) && (event?.json_data?.all_day || event?.json_data?.allDay)
                    ? moment(event.start_at_wall).tz(timezone, true)
                    : getStartDateTime(event)
            const endDateTime =
                checkIsKeepSystemDateEvent(event.type) && (event?.json_data?.all_day || event?.json_data?.allDay)
                    ? startDateTime.clone().add(event.duration, "seconds").tz(timezone, true)
                    : getEndDateTime(event)

            return {
                id: event.object_id,
                calendarId: event.calendar_id,
                start: startDateTime.format(),
                end: endDateTime.format(),
                startDateTime: startDateTime.format(),
                endDateTime: endDateTime.format(),
                originalColor,
                borderColor: borderColor,
                color: color,
                title: event.name,
                allDay,
                order: 1,
                classNames: "",
                initialEventIndex: index,
                description: event.description,
                isAppointmentSlot: event.type === CalendarEventType.AppointmentSlot,
                ownerUserId: event.owner_user_id,
                ownerFullName: event.ownerFullName,
                willGo: event.willGo,
                reminders,
                withSMS,
                withPushNotification,
                withEmail,
                eventSignInRequired,
                eventTrackAttendanceMinutes,
                attendedChangeToStatus,
                notAttendedChangeToStatus,
                completionWorkflow,
                isLesson: !isEmpty(event.courseScheduleInfo),
                type: event.type,
                isHoliday: event.type === CalendarEventType.WorkSchedule,
                schedule_suffix: event.courseScheduleInfo?.schedule_suffix,
                moduleTitle: getModuleTitle(event.json_data.eventType, event.courseInfo),
                subModuleTitle: getSubModuleTitle(event.json_data.sub_module_id, event.courseInfo),
                json_data: event.json_data,
                start_at_timezone: event.start_at_timezone,
                rrule: event.rrule
            }
        })
    }

    return {
        events,
        isLoading: isFetching || isLoadingCalendar || isLoadingSettings,
        linkedCalendars,
        staticOptions,
        primaryCalendarId: find(myCalendars, (item) => item.is_primary)?.calendar_id,
        updateLinkedCalendars: setLinkedCalendars,
        updateStaticOptions: setStaticOptions,
        setEvents,
        reloadEvents,
        setDateRange
    }
}
